import { useId, type InputHTMLAttributes, type ReactNode } from "react";
import styles from "./Field.module.css";

export interface FieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  label: ReactNode;
  /** Felt-fejl; sætter aria-invalid og kobles via aria-describedby. */
  error?: string;
  hint?: string;
}

/**
 * Input + Label + felt-fejl som ét tilgængeligt hele: label er koblet med
 * htmlFor, fejlen annonceres via role="alert" og aria-describedby.
 */
export function Field({ label, error, hint, className, ...rest }: FieldProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;

  const describedBy =
    [error !== undefined ? errorId : null, hint !== undefined ? hintId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const inputClasses = [
    styles.input,
    error !== undefined ? styles.inputError : null,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={inputClasses}
        aria-invalid={error !== undefined || undefined}
        {...(describedBy !== undefined ? { "aria-describedby": describedBy } : {})}
        {...rest}
      />
      {hint !== undefined && (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      )}
      {error !== undefined && (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
